import { useState, useEffect, useCallback, useRef } from 'react';
import {
  CalculationResult,
  Scenario,
  PredefinedScenario,
  CryptoScenario,
  BondsConfig,
  calculatePortfolio,
  calculateStatutoryPension,
  calculateNetSalary,
  findEarliestRetirementAge,
  findIntersection,
  computeTargetCapital,
  loadScenariosFromStorage,
  saveScenariosToStorage,
} from '../utils/calculation';

export function useEtfCalculator() {
  // ── Gehalt ──
  const [grossMonthlySalary, setGrossMonthlySalary] = useState(3500);
  const [localGrossMonthlySalary, setLocalGrossMonthlySalary] = useState(3500);

  // ── ETF ──
  const [currentAge, setCurrentAge] = useState(18);
  const [localCurrentAge, setLocalCurrentAge] = useState(18);
  const [etfStartValue, setEtfStartValue] = useState(0);
  const [localEtfStartValue, setLocalEtfStartValue] = useState(0);
  const [monthlyInvestment, setMonthlyInvestment] = useState(500);
  const [localMonthlyInvestment, setLocalMonthlyInvestment] = useState(500);
  const [returnRate, setReturnRate] = useState(6.0);
  const [localReturnRate, setLocalReturnRate] = useState(6.0);
  const [contributionYears, setContributionYears] = useState(20);
  const [localContributionYears, setLocalContributionYears] = useState(20);
  const [targetRetirementAge, setTargetRetirementAge] = useState(67);
  const [desiredMonthlyIncome, setDesiredMonthlyIncome] = useState(1000);
  const [localDesiredMonthlyIncome, setLocalDesiredMonthlyIncome] = useState(1000);

  // ── Cash ──
  const [cashStartAmount, setCashStartAmount] = useState(0);
  const [localCashStartAmount, setLocalCashStartAmount] = useState(0);
  const [cashAnnualRate, setCashAnnualRate] = useState(2.5);
  const [localCashAnnualRate, setLocalCashAnnualRate] = useState(2.5);

  // ── Crypto ──
  const [cryptoEnabled, setCryptoEnabled] = useState(false);
  const [btcAmount, setBtcAmount] = useState(0);
  const [localBtcAmount, setLocalBtcAmount] = useState(0);
  const [btcBuyPrice, setBtcBuyPrice] = useState(30000);
  const [localBtcBuyPrice, setLocalBtcBuyPrice] = useState(30000);
  const [cryptoScenario, setCryptoScenario] = useState<CryptoScenario>('base');

  // ── Real estate ──
  const [realEstateEnabled, setRealEstateEnabled] = useState(false);
  const [purchasePrice, setPurchasePrice] = useState(300000);
  const [localPurchasePrice, setLocalPurchasePrice] = useState(300000);
  const [annualAppreciation, setAnnualAppreciation] = useState(2.0);
  const [localAnnualAppreciation, setLocalAnnualAppreciation] = useState(2.0);
  const [isRental, setIsRental] = useState(false);
  const [monthlyRent, setMonthlyRent] = useState(800);
  const [localMonthlyRent, setLocalMonthlyRent] = useState(800);
  const [monthlyExpenses, setMonthlyExpenses] = useState(200);
  const [localMonthlyExpenses, setLocalMonthlyExpenses] = useState(200);
  const [hasLoan, setHasLoan] = useState(false);
  const [loanAmount, setLoanAmount] = useState(240000);
  const [localLoanAmount, setLocalLoanAmount] = useState(240000);
  const [loanInterestRate, setLoanInterestRate] = useState(3.5);
  const [localLoanInterestRate, setLocalLoanInterestRate] = useState(3.5);
  const [loanTermYears, setLoanTermYears] = useState(25);
  const [localLoanTermYears, setLocalLoanTermYears] = useState(25);

  // ── Bonds ──
  const [bondsEnabled, setBondsEnabled] = useState(false);
  const [bondsStartAmount, setBondsStartAmount] = useState(0);
  const [localBondsStartAmount, setLocalBondsStartAmount] = useState(0);
  const [bondsCouponRate, setBondsCouponRate] = useState(3.0);
  const [localBondsCouponRate, setLocalBondsCouponRate] = useState(3.0);

  // ── UI / general ──
  const [chartData, setChartData] = useState<CalculationResult[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [inflationRate, setInflationRate] = useState(2.0);
  const [showRealValues, setShowRealValues] = useState(false);
  const [taxRate] = useState(26.375);
  const [taxFreibetrag, setTaxFreibetrag] = useState(801);
  const [showTaxedValues, setShowTaxedValues] = useState(false);
  const [scenarios, setScenarios] = useState<Scenario[]>(() => loadScenariosFromStorage());
  const [isScenarioDialogOpen, setIsScenarioDialogOpen] = useState(false);
  const [newScenarioName, setNewScenarioName] = useState('');
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [hasAutoSetRetirementAge, setHasAutoSetRetirementAge] = useState(false);
  const [retirementAgeManuallySet, setRetirementAgeManuallySet] = useState(false);

  // Persist scenarios
  useEffect(() => { saveScenariosToStorage(scenarios); }, [scenarios]);

  const bondsConfig: BondsConfig = { enabled: bondsEnabled, startAmount: bondsStartAmount, couponRate: bondsCouponRate };

  const recalculate = useCallback(() => {
    try {
      const results = calculatePortfolio({
        currentAge, grossMonthlySalary, etfStartValue, monthlyInvestment, returnRate,
        contributionYears, targetRetirementAge, desiredMonthlyIncome,
        inflationRate, taxRate, taxFreibetrag,
        cash: { startAmount: cashStartAmount, annualRate: cashAnnualRate },
        crypto: { enabled: cryptoEnabled, btcAmount, buyPriceEur: btcBuyPrice, scenario: cryptoScenario },
        realEstate: {
          enabled: realEstateEnabled, purchasePrice, annualAppreciation,
          isRental, monthlyRent, monthlyExpenses,
          hasLoan, loanAmount, loanInterestRate, loanTermYears,
        },
        bonds: bondsConfig,
      });
      setChartData(results);
    } catch (error) {
      console.error('Fehler bei der Vermögensberechnung:', error);
      setChartData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAge, grossMonthlySalary, etfStartValue, monthlyInvestment, returnRate,
    contributionYears, targetRetirementAge, desiredMonthlyIncome,
    inflationRate, taxRate, taxFreibetrag,
    cashStartAmount, cashAnnualRate,
    cryptoEnabled, btcAmount, btcBuyPrice, cryptoScenario,
    realEstateEnabled, purchasePrice, annualAppreciation, isRental, monthlyRent, monthlyExpenses,
    hasLoan, loanAmount, loanInterestRate, loanTermYears,
    bondsEnabled, bondsStartAmount, bondsCouponRate,
  ]);

  useEffect(() => { recalculate(); }, [recalculate]);

  // Prevent feedback loops: skip one auto-retirement cycle after we just set it
  const skipAutoRetirement = useRef(false);
  // Das Rentenalter fließt selbst in die Rechnung ein (Ansparphase, gesetzliche
  // Rente). Dadurch kann das früheste Rentenalter zwischen zwei Werten pendeln:
  // bei 53 reicht es schon ab 52, bei 52 erst ab 53. Der letzte automatische
  // Sprung wird gemerkt; will die Automatik genau zurückspringen, bleibt sie
  // beim höheren (sicheren) der beiden Werte stehen.
  const lastAutoJump = useRef<{ from: number; to: number } | null>(null);

  // Ändert sich eine echte Eingabe, gilt die Pendel-Sperre nicht mehr.
  useEffect(() => {
    lastAutoJump.current = null;
  }, [
    currentAge, grossMonthlySalary, etfStartValue, monthlyInvestment, returnRate,
    contributionYears, desiredMonthlyIncome, inflationRate, taxFreibetrag,
    cashStartAmount, cashAnnualRate,
    cryptoEnabled, btcAmount, btcBuyPrice, cryptoScenario,
    realEstateEnabled, purchasePrice, annualAppreciation, isRental, monthlyRent, monthlyExpenses,
    hasLoan, loanAmount, loanInterestRate, loanTermYears,
    bondsEnabled, bondsStartAmount, bondsCouponRate, retirementAgeManuallySet,
  ]);

  useEffect(() => {
    if (chartData.length === 0) return;

    // Bounds: retirement age must be > currentAge and >= 30
    const minRetirementAge = Math.max(currentAge + 1, 30);
    if (targetRetirementAge < minRetirementAge) {
      skipAutoRetirement.current = true;
      setTargetRetirementAge(minRetirementAge);
      return;
    }

    // Bounds: contribution years can't exceed time until retirement
    const maxYears = Math.max(1, targetRetirementAge - currentAge);
    if (contributionYears > maxYears) {
      setContributionYears(maxYears);
      return;
    }

    // Auto-retirement: only if not manually set, and not in a feedback cycle
    if (skipAutoRetirement.current) {
      skipAutoRetirement.current = false;
      return;
    }

    if (!retirementAgeManuallySet) {
      let earliestAge = findEarliestRetirementAge(chartData, currentAge, desiredMonthlyIncome, returnRate);
      const jump = lastAutoJump.current;
      if (earliestAge && jump && jump.to === targetRetirementAge && earliestAge === jump.from) {
        earliestAge = Math.max(jump.from, jump.to);
      }
      if (earliestAge && earliestAge !== targetRetirementAge) {
        skipAutoRetirement.current = true;
        lastAutoJump.current = { from: targetRetirementAge, to: earliestAge };
        setTargetRetirementAge(earliestAge);
        if (!hasAutoSetRetirementAge) setHasAutoSetRetirementAge(true);
        return;
      }
    }

    if (!hasAutoSetRetirementAge) setHasAutoSetRetirementAge(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, currentAge, targetRetirementAge, contributionYears, retirementAgeManuallySet]);

  // Sync local slider states
  useEffect(() => { setLocalGrossMonthlySalary(grossMonthlySalary); }, [grossMonthlySalary]);
  useEffect(() => { setLocalCurrentAge(currentAge); }, [currentAge]);
  useEffect(() => { setLocalEtfStartValue(etfStartValue); }, [etfStartValue]);
  useEffect(() => { setLocalMonthlyInvestment(monthlyInvestment); }, [monthlyInvestment]);
  useEffect(() => { setLocalContributionYears(Math.max(1, contributionYears)); }, [contributionYears]);
  useEffect(() => { setLocalReturnRate(returnRate); }, [returnRate]);
  useEffect(() => { setLocalDesiredMonthlyIncome(desiredMonthlyIncome); }, [desiredMonthlyIncome]);
  useEffect(() => { setLocalCashStartAmount(cashStartAmount); }, [cashStartAmount]);
  useEffect(() => { setLocalCashAnnualRate(cashAnnualRate); }, [cashAnnualRate]);
  useEffect(() => { setLocalBtcAmount(btcAmount); }, [btcAmount]);
  useEffect(() => { setLocalBtcBuyPrice(btcBuyPrice); }, [btcBuyPrice]);
  useEffect(() => { setLocalPurchasePrice(purchasePrice); }, [purchasePrice]);
  useEffect(() => { setLocalAnnualAppreciation(annualAppreciation); }, [annualAppreciation]);
  useEffect(() => { setLocalMonthlyRent(monthlyRent); }, [monthlyRent]);
  useEffect(() => { setLocalMonthlyExpenses(monthlyExpenses); }, [monthlyExpenses]);
  useEffect(() => { setLocalLoanAmount(loanAmount); }, [loanAmount]);
  useEffect(() => { setLocalLoanInterestRate(loanInterestRate); }, [loanInterestRate]);
  useEffect(() => { setLocalLoanTermYears(loanTermYears); }, [loanTermYears]);
  useEffect(() => { setLocalBondsStartAmount(bondsStartAmount); }, [bondsStartAmount]);
  useEffect(() => { setLocalBondsCouponRate(bondsCouponRate); }, [bondsCouponRate]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);

  const getCurrentScenarioData = (): Omit<Scenario, 'id' | 'name'> => ({
    currentAge, initialInvestment: etfStartValue, monthlyInvestment, returnRate,
    contributionYears, targetRetirementAge, desiredMonthlyIncome, chartData,
  });

  const saveScenario = () => {
    const newScenario: Scenario = { id: Date.now().toString(), name: newScenarioName, ...getCurrentScenarioData() };
    setScenarios([...scenarios, newScenario]);
    setNewScenarioName('');
    setIsScenarioDialogOpen(false);
  };

  const deleteScenario = (id: string) => {
    setScenarios(scenarios.filter((s) => s.id !== id));
    setSelectedScenarios(selectedScenarios.filter((s) => s !== id));
  };

  const loadScenario = (scenario: Scenario) => {
    setCurrentAge(scenario.currentAge);
    setEtfStartValue(scenario.initialInvestment);
    setMonthlyInvestment(scenario.monthlyInvestment);
    setReturnRate(scenario.returnRate);
    setContributionYears(scenario.contributionYears);
    setTargetRetirementAge(scenario.targetRetirementAge);
    setDesiredMonthlyIncome(scenario.desiredMonthlyIncome);
    // Das gespeicherte Rentenalter gilt — sonst überschreibt es die Automatik sofort.
    setRetirementAgeManuallySet(true);
  };

  const toggleScenarioSelection = (id: string) => {
    setSelectedScenarios((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id].slice(-2));
  };

  const applyPredefinedScenario = (scenario: PredefinedScenario) => {
    setReturnRate(scenario.returnRate);
    setInflationRate(scenario.inflationRate);
  };

  const workingYears = Math.max(0, targetRetirementAge - currentAge);
  const statutoryPension = calculateStatutoryPension(grossMonthlySalary, workingYears);
  const netSalary = calculateNetSalary(grossMonthlySalary);
  const availableAfterInvesting = netSalary.netMonthly - monthlyInvestment;

  const intersection = findIntersection(chartData, currentAge, desiredMonthlyIncome, returnRate);
  const zielLinieY = computeTargetCapital(intersection, targetRetirementAge, desiredMonthlyIncome, returnRate);

  const maxY = Math.max(
    ...chartData.map((d) => d.totalValue || 0),
    ...chartData.map((d) => d.totalInvestment || 0),
    zielLinieY || 0, 100_000
  ) + 50_000;

  return {
    grossMonthlySalary, setGrossMonthlySalary, localGrossMonthlySalary, setLocalGrossMonthlySalary,
    statutoryPension, netSalary, availableAfterInvesting,
    currentAge, setCurrentAge, localCurrentAge, setLocalCurrentAge,
    etfStartValue, setEtfStartValue, localEtfStartValue, setLocalEtfStartValue,
    monthlyInvestment, setMonthlyInvestment, localMonthlyInvestment, setLocalMonthlyInvestment,
    returnRate, setReturnRate, localReturnRate, setLocalReturnRate,
    contributionYears, setContributionYears, localContributionYears, setLocalContributionYears,
    desiredMonthlyIncome, setDesiredMonthlyIncome, localDesiredMonthlyIncome, setLocalDesiredMonthlyIncome,
    targetRetirementAge, setTargetRetirementAge,

    cashStartAmount, setCashStartAmount, localCashStartAmount, setLocalCashStartAmount,
    cashAnnualRate, setCashAnnualRate, localCashAnnualRate, setLocalCashAnnualRate,

    cryptoEnabled, setCryptoEnabled,
    btcAmount, setBtcAmount, localBtcAmount, setLocalBtcAmount,
    btcBuyPrice, setBtcBuyPrice, localBtcBuyPrice, setLocalBtcBuyPrice,
    cryptoScenario, setCryptoScenario,

    realEstateEnabled, setRealEstateEnabled,
    purchasePrice, setPurchasePrice, localPurchasePrice, setLocalPurchasePrice,
    annualAppreciation, setAnnualAppreciation, localAnnualAppreciation, setLocalAnnualAppreciation,
    isRental, setIsRental,
    monthlyRent, setMonthlyRent, localMonthlyRent, setLocalMonthlyRent,
    monthlyExpenses, setMonthlyExpenses, localMonthlyExpenses, setLocalMonthlyExpenses,
    hasLoan, setHasLoan,
    loanAmount, setLoanAmount, localLoanAmount, setLocalLoanAmount,
    loanInterestRate, setLoanInterestRate, localLoanInterestRate, setLocalLoanInterestRate,
    loanTermYears, setLoanTermYears, localLoanTermYears, setLocalLoanTermYears,

    bondsEnabled, setBondsEnabled,
    bondsStartAmount, setBondsStartAmount, localBondsStartAmount, setLocalBondsStartAmount,
    bondsCouponRate, setBondsCouponRate, localBondsCouponRate, setLocalBondsCouponRate,

    chartData, tabValue, handleTabChange,
    inflationRate, setInflationRate,
    showRealValues, setShowRealValues,
    taxRate, taxFreibetrag, setTaxFreibetrag,
    showTaxedValues, setShowTaxedValues,
    retirementAgeManuallySet, setRetirementAgeManuallySet,

    scenarios, isScenarioDialogOpen, setIsScenarioDialogOpen,
    newScenarioName, setNewScenarioName, selectedScenarios,
    saveScenario, deleteScenario, loadScenario,
    toggleScenarioSelection, applyPredefinedScenario,

    intersection, zielLinieY, maxY,
  };
}
